import { createSignal, onMount, Show } from "solid-js";
import { trackBeacon } from "~/lib/analytics";
import { rememberReturnTo, startLogin } from "~/lib/auth";
import { config, isAuthConfigured, isApiConfigured } from "~/lib/config";
import { meetupPath, meetupSlugFromLocation } from "~/lib/meetup";

export function LoginPage() {
  const [loginError, setLoginError] = createSignal("");
  const [meetupSlug, setMeetupSlug] = createSignal("");

  onMount(() => {
    const slug = meetupSlugFromLocation(window.location.pathname, window.location.search);
    if (slug) {
      setMeetupSlug(slug);
      rememberReturnTo(meetupPath(slug));
    }
  });

  async function onLogin() {
    setLoginError("");
    trackBeacon("login_started", undefined, { page: "/login" });
    try {
      await startLogin();
    } catch (err) {
      const message = (err as Error).message || String(err);
      if (/crypto\.subtle|SubtleCrypto|secure context/i.test(message)) {
        setLoginError(
          "Sign-in needs a full browser on HTTPS. Open https://connect.dataengbytes.com in Safari or Chrome (not Slack/LinkedIn in-app browser), then try again.",
        );
        return;
      }
      setLoginError(message);
    }
  }

  return (
    <div class="login-page">
      <section class="card">
        <p class="status-chip">{meetupSlug() ? "Meetup registration" : "Attendee networking"}</p>
        <h1 class="gradient-text">DataEngBytes Connect</h1>
        <Show
          when={meetupSlug()}
          fallback={
            <p>
              Use the email on your conference ticket. First time here? Register with that email,
              then sign in. Magic link, password, and Google supported.
            </p>
          }
        >
          <p>
            Sign in to register for this meetup and show your door QR. Free — not a conference
            ticket. Magic link, password, and Google supported.
          </p>
        </Show>

        {!isAuthConfigured() && (
          <div class="error-box">
            Auth is not configured yet. Add <code>VITE_ZITADEL_CLIENT_ID</code> to{" "}
            <code>.env.local</code> (see <code>.env.example</code>).
          </div>
        )}

        {!isApiConfigured() && (
          <div class="error-box">
            Set <code>VITE_DEB_API_URL</code> in <code>.env.local</code>.
          </div>
        )}

        {isAuthConfigured() && !config.zitadelOrgDomain && !config.zitadelOrgId && (
          <p class="muted">
            Tip: set <code>VITE_ZITADEL_ORG_DOMAIN</code> (or <code>VITE_ZITADEL_ORG_ID</code>) in{" "}
            <code>.env.local</code> so login targets your Zitadel organisation.
          </p>
        )}

        {loginError() && <div class="error-box">{loginError()}</div>}

        <div class="sign-in-card">
          <p class="muted">Secure sign-in via Zitadel</p>
          <button
            type="button"
            class="btn btn-primary"
            onClick={onLogin}
            disabled={!isAuthConfigured()}
          >
            Sign in
          </button>
          <div class="auth-method-chips">
            <span class="auth-method-chip">Magic link</span>
            <span class="auth-method-chip">Password</span>
            <span class="auth-method-chip">Google</span>
          </div>
        </div>

        <p class="muted" style={{ "text-align": "center", "margin-top": "24px" }}>
          <a href="/help#claim">How to claim your ticket</a>
          {" · "}
          <a href="/help">FAQ</a>
        </p>
        <p class="muted" style={{ "text-align": "center", "margin-top": "12px" }}>
          Part of the DataEngBytes community — run by practitioners, for practitioners.
        </p>
      </section>
    </div>
  );
}
